'use client';

import { useEffect, useRef, useCallback } from 'react';
import { Loader2, Wifi, WifiOff } from 'lucide-react';
import { TrendCard } from './trend-card';
import { useScoredTrends } from '@/hooks/use-trends';
import { useWebSocket } from '@/hooks/use-websocket';
import { useQueryClient } from '@tanstack/react-query';
import type { TrendFilters } from '@/hooks/use-trends';

interface TrendGridProps {
  filters: TrendFilters;
}

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-lg border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded bg-muted" />
          <div className="h-4 w-8 rounded bg-muted" />
        </div>
        <div className="h-8 w-8 rounded-full bg-muted" />
      </div>
      <div className="h-5 w-3/4 rounded bg-muted" />
      <div className="h-5 w-1/2 rounded bg-muted" />
      <div className="h-4 w-1/4 rounded bg-muted" />
      <div className="h-4 w-full rounded bg-muted" />
      <div className="flex justify-between">
        <div className="h-4 w-8 rounded bg-muted" />
        <div className="h-4 w-4 rounded bg-muted" />
      </div>
    </div>
  );
}

export function TrendGrid({ filters }: TrendGridProps) {
  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useScoredTrends(filters);

  const { isConnected, newTrends, clearNewTrends } = useWebSocket();
  const queryClient = useQueryClient();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const mergeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Merge new WebSocket trends into query data after 10 seconds
  const mergeNewTrends = useCallback(() => {
    if (newTrends.length > 0) {
      clearNewTrends();
      queryClient.invalidateQueries({ queryKey: ['scored-trends'] });
    }
  }, [newTrends.length, clearNewTrends, queryClient]);

  // Auto-merge after timeout when new trends arrive
  useEffect(() => {
    if (newTrends.length > 0 && !mergeTimeoutRef.current) {
      mergeTimeoutRef.current = setTimeout(() => {
        mergeNewTrends();
        mergeTimeoutRef.current = null;
      }, 10_000);
    }
    return () => {
      if (mergeTimeoutRef.current) {
        clearTimeout(mergeTimeoutRef.current);
        mergeTimeoutRef.current = null;
      }
    };
  }, [newTrends.length, mergeNewTrends]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const allTrends = data?.pages.flat() ?? [];

  // Deduplicate: exclude any newTrends already present in paginated data
  const existingIds = new Set(allTrends.map((t) => t.id));
  const uniqueNewTrends = newTrends.filter((t) => !existingIds.has(t.id));

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  // Empty state
  if (allTrends.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-lg text-muted-foreground">Ничего не найдено</p>
        <button
          onClick={() => {
            // Clear all URL params
            if (typeof window !== 'undefined') {
              window.history.replaceState(null, '', window.location.pathname);
              window.location.reload();
            }
          }}
          className="mt-2 text-sm text-primary hover:underline"
        >
          Сбросить фильтры
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* WebSocket connection indicator */}
      <div className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
        {isConnected ? (
          <>
            <Wifi className="h-3 w-3 text-green-400" />
            <span>Live</span>
          </>
        ) : (
          <>
            <WifiOff className="h-3 w-3" />
            <span>Offline</span>
          </>
        )}
        {uniqueNewTrends.length > 0 && (
          <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-primary">
            +{uniqueNewTrends.length} new
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {/* New trends from WebSocket with entry animation */}
        {uniqueNewTrends.map((trend) => (
          <div
            key={`ws-${trend.id}`}
            className="animate-in fade-in slide-in-from-top-2 duration-500"
          >
            <TrendCard trend={trend} />
          </div>
        ))}

        {/* Existing paginated trends */}
        {allTrends.map((trend) => (
          <TrendCard key={trend.id} trend={trend} />
        ))}
      </div>

      {/* Sentinel for infinite scroll */}
      <div ref={sentinelRef} className="h-4" />

      {/* Loading more indicator */}
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}
